import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, FormControlLabel,
  TextField, Grid, Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import { useStackZIndex, ModalStackProvider } from '../../utilities/ModalStackContext';

const TRUCK_ITEMS = [
  { key: 'lights', label: 'Lights', type: 'checkbox' },
  { key: 'fuel_level', label: 'Fuel Level', type: 'checkbox' },
  { key: 'oil_life', label: 'Oil Life', type: 'text', placeholder: 'e.g. 80%' },
  { key: 'tires', label: 'Tires', type: 'checkbox' },
  { key: 'walk_around', label: 'Full walk around — note any scratches, dings, dents, etc.', type: 'checkbox' },
  { key: 'anything_stand_out', label: 'Anything stand out?', type: 'text', placeholder: 'Notes...' },
];

const RETURN_EXTRA_TRUCK_ITEMS = [
  { key: 'clean_truck_trailer', label: 'Clean Truck and Trailer', type: 'checkbox' },
];

const TRAILER_ITEMS = [
  { key: 'running_lights', label: 'Running Lights', type: 'checkbox' },
  { key: 'brake_lights', label: 'Brake Lights', type: 'checkbox' },
  { key: 'turn_signals', label: 'Turn Signals', type: 'checkbox' },
  { key: 'tires', label: 'Tires', type: 'checkbox' },
  { key: 'spare', label: 'Spare', type: 'checkbox' },
  { key: 'jump_box', label: 'Jump Box', type: 'checkbox' },
  { key: 'flares', label: 'Flares', type: 'checkbox' },
  { key: 'triangles', label: 'Triangles', type: 'checkbox' },
  { key: 'ramps', label: 'Ramps', type: 'checkbox' },
  { key: 'winch', label: 'Winch', type: 'checkbox' },
  { key: 'winch_remote_functional', label: 'Winch Remote (functional)', type: 'checkbox' },
  { key: 'winch_remote_location', label: 'Winch Remote Location', type: 'text', placeholder: 'Where was it left?', returnOnly: true },
  { key: 'straps_count', label: 'Straps (how many total)', type: 'text', placeholder: 'e.g. 8' },
  { key: 'siding_condition', label: 'Trailer Siding Condition', type: 'checkbox' },
];

const defaultTruck = (type) => {
  const base = {
    lights: false,
    fuel_level: false,
    oil_life: '',
    tires: false,
    walk_around: false,
    anything_stand_out: '',
  };
  if (type === 'return') return { ...base, clean_truck_trailer: false };
  return base;
};

const defaultTrailer = () => ({
  running_lights: false,
  brake_lights: false,
  turn_signals: false,
  tires: false,
  spare: false,
  jump_box: false,
  flares: false,
  triangles: false,
  ramps: false,
  winch: false,
  winch_remote_functional: false,
  winch_remote_location: '',
  straps_count: '',
  siding_condition: false,
});

const ChecklistDialog = ({ open, onClose, type, loadId, existingData, onSaved }) => {
  const myZ = useStackZIndex();
  const fieldName = type === 'departure' ? 'departure_checklist' : 'return_checklist';
  const title = type === 'departure' ? 'Departure Checklist' : 'Return Checklist';

  const [truck, setTruck] = useState(defaultTruck(type));
  const [trailer, setTrailer] = useState(defaultTrailer());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (existingData) {
        setTruck({ ...defaultTruck(type), ...(existingData.truck || {}) });
        setTrailer({ ...defaultTrailer(), ...(existingData.trailer || {}) });
      } else {
        setTruck(defaultTruck(type));
        setTrailer(defaultTrailer());
      }
    }
  }, [existingData, type, open]);

  const handleSave = async (markComplete = false) => {
    setSaving(true);
    const checklistData = {
      completed: markComplete ? true : (existingData?.completed || false),
      completed_at: markComplete ? new Date().toISOString() : (existingData?.completed_at || null),
      completed_by: markComplete ? StateManager.userID : (existingData?.completed_by || null),
      truck,
      trailer,
    };
    try {
      await firebase.firestore().collection('shipping-loads').doc(loadId).update({
        [fieldName]: checklistData,
      });
      onSaved(fieldName, checklistData);
      StateManager.setAlertAndOpen(`${title} saved`, 'success');
      if (markComplete) onClose();
    } catch (err) {
      console.error('Error saving checklist:', err);
      StateManager.setAlertAndOpen('Error saving checklist', 'error');
    } finally {
      setSaving(false);
    }
  };

  const truckItems = type === 'return'
    ? [...TRUCK_ITEMS, ...RETURN_EXTRA_TRUCK_ITEMS]
    : TRUCK_ITEMS;

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {title}
          {existingData?.completed && (
            <Chip
              label="Completed"
              color="success"
              size="small"
              icon={<CheckCircleIcon />}
            />
          )}
        </Box>
        {existingData?.completed_at && (
          <Typography variant="caption" color="text.secondary" display="block">
            Completed {new Date(existingData.completed_at).toLocaleString()}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={4}>
          {/* Truck Column */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Truck
            </Typography>
            {truckItems.map(item =>
              item.type === 'checkbox' ? (
                <FormControlLabel
                  key={item.key}
                  control={
                    <Checkbox
                      checked={!!truck[item.key]}
                      onChange={(e) => setTruck(prev => ({ ...prev, [item.key]: e.target.checked }))}
                    />
                  }
                  label={item.label}
                  sx={{ display: 'flex', mb: 0.5 }}
                />
              ) : (
                <TextField
                  key={item.key}
                  label={item.label}
                  value={truck[item.key] || ''}
                  onChange={(e) => setTruck(prev => ({ ...prev, [item.key]: e.target.value }))}
                  placeholder={item.placeholder}
                  size="small"
                  fullWidth
                  sx={{ mb: 1.5, mt: 0.5 }}
                />
              )
            )}
          </Grid>

          {/* Trailer Column */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Trailer
            </Typography>
            {TRAILER_ITEMS.filter(item => !item.returnOnly || type === 'return').map(item =>
              item.type === 'checkbox' ? (
                <FormControlLabel
                  key={item.key}
                  control={
                    <Checkbox
                      checked={!!trailer[item.key]}
                      onChange={(e) => setTrailer(prev => ({ ...prev, [item.key]: e.target.checked }))}
                    />
                  }
                  label={item.label}
                  sx={{ display: 'flex', mb: 0.5 }}
                />
              ) : (
                <TextField
                  key={item.key}
                  label={item.label}
                  value={trailer[item.key] || ''}
                  onChange={(e) => setTrailer(prev => ({ ...prev, [item.key]: e.target.value }))}
                  placeholder={item.placeholder}
                  size="small"
                  fullWidth
                  sx={{ mb: 1.5, mt: 0.5 }}
                />
              )
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => handleSave(false)} disabled={saving} variant="outlined">
          Save
        </Button>
        <Button
          onClick={() => handleSave(true)}
          disabled={saving}
          variant="contained"
          color="success"
        >
          {existingData?.completed ? 'Update & Mark Complete' : 'Save & Mark Complete'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
};

export default ChecklistDialog;
